/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */

import { COLOR_MODE, SDKDataFormat } from '@ouster/webviz';
import {
  AccessLevel,
  ListDriveInfo,
  RawDriveInfo as RawListDriveInfo,
  RecordingAccessConfigInfo,
} from '@ouster/webviz-react-components';

import {
  ROLES,
  INVITATION_STATUS,
  FILTER_KEY_LABEL_MAP,
} from './utils/constants';
import { CalibrationStatus } from './utils/misc';

export type User = {
  name: string;
  email: string;
  // access token. Not parsed just a string
  accessToken: string;
  identity_id: string;
};

export type UserRole = typeof ROLES[number];
export type InvitationStatus = typeof INVITATION_STATUS[number];

export type UserWithRole = {
  email: string;
  id: number;
  identity_id: string;
  organization_id: number;
  role: UserRole;
  name: string;
  invitation_status?: InvitationStatus;
  invitation_link?: string;
  uuid?: string; // organization uuid
};

export type UserOrg = {
  name: string;
  role: UserRole;
  id: number; // organization_id
  uuid: string; // organization uuid
  is_provisioned: boolean;
  provisioned_by_id: number | null;
  handed_over_at: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'DELETED' | 'MERGED';
};

export type AppUser = {
  name: string;
  email: string;
  identity_id: string;
  id: number; // user id in db
  organization_id: number;
  organization_uuid: string; // organization uuid
  email_opt_out?: boolean; // user opted out of marketing emails
};

/* 
  allowed_space = 1 means the org doesn't have usage limitation
*/
export type OrgSpaceUsage = {
  allowed_space: number;
  space_used: number;
};

// TODO(pierre) on 2021-05-05: Update to enum
export type LoadStatus =
  | 'notStarted'
  | 'loading'
  | 'success'
  | 'failed'
  | 'noTasksLeft';

export enum Status {
  Finished = 'FINISHED',
  Failed = 'FAILED',
  NotStarted = 'NOT_STARTED',
  Running = 'RUNNING',
}
export enum DataType {
  Sdk = 'SDK',
}
export enum RecordingStatus {
  Processing = 'Processing...',
  Failed = 'Failed',
  SlamError = 'SLAM error',
  Ready = 'Ready',
  NoStatus = '',
}
/**
 *
 * @param field
 */
export function isDataType(field: string) {
  return Object.values(DataType).includes(field as DataType);
}

export type IsoDateTime = string;

export type RawDriveSensorInfo = {
  sdk_fw_branch: string | null;
  sdk_fw_build_date: IsoDateTime | null;
  sdk_fw_build_rev: string | null;
  sdk_fw_commit_hash: string | null;
  sdk_fw_image_rev: string | null;
  sdk_fw_merge_request: string | null;
  sdk_fw_version_core: string | null;
  sdk_fw_version_prerelease: string | null;
  sdk_lidar_mode: string | null;
  sdk_prod_beam_spacing: string | null;
  sdk_prod_generation: string | null;
  sdk_prod_hw_rev: string | null;
  sdk_prod_line: string | null;
  sdk_prod_pn: string | null;
  sdk_prod_resolution: number | null;
  sdk_prod_sn: string | null;
  sdk_prod_type: string | null;
};

export type DriveSensorInfo = {
  sdkFwBranch: string | null;
  sdkFwBuildDate: Date | null;
  sdkFwBuildRev: string | null;
  sdkFwCommitHash: string | null;
  sdkFwImageRev: string | null;
  sdkFwMergeRequest: string | null;
  sdkFwVersionCore: string | null;
  sdkFwVersionPrerelease: string | null;
  sdkLidarMode: string | null;
  sdkProductBeamSpacing: string | null;
  sdkProductGeneration: string | null;
  sdkProductHwRevision: string | null;
  sdkProductLine: string | null;
  sdkProductPartNumber: string | null;
  sdkProductResolution: number | null;
  sdkProductSerialNumber: string | null;
  sdkProductType: string | null;
};

export type RawDriveInfo = {
  id: number | string;
  data_type: DataType;
  drive_origin?: string | null;
  organization_id: number;
  upload_status: Status;
  source_bucket: string;
  source_prefix: string;
  // Not null column in the DB, but has a default value in the DB so
  // effectively won't be null
  processed_at: IsoDateTime;
  // Fields below are optional in the Db, and the API response might
  // fill them as null.
  data_category: string | null;
  start_time: IsoDateTime | null;
  stop_time: IsoDateTime | null;
  osf_bucket: string | null;
  osf_prefix: string | null;
  osf_generation_date: IsoDateTime | null;
  osf_generation_status: Status;
  slam_bucket: string | null;
  slam_prefix: string | null;
  slam_generation_date: IsoDateTime | null;
  slam_generation_status: Status;
  uploaded_by: string | null;
  uploaded_by_id: number | null;
  // publication_uuid will only be returned in the drives/ endpoint but
  // not on the drives/<drive_id> endpoint so why we make it optional
  // here
  publication_uuid?: string | null;
  public_title: string | null;
  access_level?: AccessLevel;
  sensor_info: RawDriveSensorInfo[];
  tags: string[];
  visualization_status: string;
  organization_name?: string | null;
  recording_title?: string;
};

export type RecordingPublicityAccessConfig = RecordingAccessConfigInfo & {
  recording_title?: string;
};

export type DriveInfo = {
  id: number | string;
  dataType: DataType;
  driveOrigin?: string | null;
  organizationId: number;
  uploadStatus: Status;
  sourceBucket: string;
  sourcePrefix: string;
  processedAt: Date;
  dataCategory: string | null;
  startTime: Date | '...';
  stopTime: Date | '...';
  osfBucket: string | null;
  osfPrefix: string | null;
  osfGenerationStatus: Status;
  osfGenerationDate: Date | null;
  slamBucket: string | null;
  slamPrefix: string | null;
  slamGenerationStatus: Status;
  slamGenerationDate: Date | null;
  uploadedBy: string | null;
  uploadedById: number | null;
  publicTitle: string | null;
  accessLevel?: AccessLevel;
  publicURL?: string;
  sensorInfos: DriveSensorInfo[];
  tags: string[];
  visualization_status: string;
  organizationName?: string | null;
  recordingTitle?: string;
};

export type RawDriveListApiResponse = {
  data: Array<RawListDriveInfo>;
  total_count: number;
  // Optional pagination fields (present in search responses)
  current_page?: number;
  total_pages?: number;
  next_page?: {
    page: number;
    per_page: number;
    q?: string;
  };
  previous_page?: {
    page: number;
    per_page: number;
    q?: string;
  };
  query?: string;
};

export type DriveListApiResponse = {
  data: Array<ListDriveInfo>;
  meta: {
    totalCount: number;
  };
};

export type FilterKey = keyof typeof FILTER_KEY_LABEL_MAP;

export interface Output {
  name: string;
  bucket: string | null; // null if processing failed
  prefix: string | null;
  generationDate: Date;
  generationStatus: string;
}

export interface DropdownSectionInfo {
  name: string;
  description?: string;
}

export interface DropdownData {
  name: string;
  description?: string;
}

export interface TagCategory extends DropdownSectionInfo {
  id: number;
}

export interface Tag extends DropdownData {
  id: number;
  category: string;
}

export interface DataCategory extends DropdownData {
  id: number;
  category: string;
}

export type BaseMappedType = {
  label: string;
  value: string;
};

export type MappedDataCategory = BaseMappedType & {
  data: DataCategory;
};
export type NullableMappedDataCategory = MappedDataCategory | null;

export type MappedTag = BaseMappedType & {
  data: Tag;
};

export type MappedFilterOption = BaseMappedType & {
  data: null;
};

export type PaginationInfo = {
  currentPage: number;
  perPage: number;
  totalPages: number;
  totalCount: number;
  nextPageUrl: string | null;
  previousPageUrl: string | null;
};

// Avoid using string enums here to store the column name in order to
// avoid name collisions
export enum DriveTableColumn {
  SDK_RECORDED_DATE,
  DUR,
  SDK_UPLOADER,
  SDK_UPLOADED_DATE,
  SENSOR,
  HW,
  FW,
  MODE,
  CATEGORY,
  SDK_TAGS,
  ACTION,
}

export enum SdkFilterableField {
  data_category = 'data_category',
  sdk_fw_branch = 'sdk_fw_branch',
  sdk_fw_build_rev = 'sdk_fw_build_rev',
  sdk_fw_version_core = 'sdk_fw_version_core',
  sdk_prod_sn = 'sdk_prod_sn',
  sdk_lidar_mode = 'sdk_lidar_mode',
  sdk_prod_beamspacing = 'sdk_prod_beam_spacing',
  sdk_prod_generation = 'sdk_prod_generation',
  sdk_prod_hw_rev = 'sdk_prod_hw_rev',
  sdk_prod_resolution = 'sdk_prod_resolution',
  sdk_prod_type = 'sdk_prod_type',
  sdk_fw_image_rev = 'sdk_fw_image_rev',
  tags = 'tags',
  uploaded_by = 'uploaded_by',
}

export const ExtendedSdkWithOusterFilterableField = {
  ...SdkFilterableField,
  shared_by_name: 'shared_by_name',
  organization_name: 'organization_name',
} as const;

export const ExtendedSdkFromOusterFilterableField = {
  ...SdkFilterableField,
  organization_name: 'organization_name',
} as const;

export type FilterableField = SdkFilterableField;

/**
 *
 * @param field
 */
export function isFilterableField(field: string) {
  return Object.values(SdkFilterableField).includes(
    field as SdkFilterableField
  );
}

export enum OrderableField {
  processed_at = 'processed_at',
  start_time = 'start_time',
  created_at_asc = 'created_at&order=asc',
  created_at_dsc = 'created_at&order=desc',
}

/**
 *
 * @param field
 */
export function isOrderableField(field: string) {
  return Object.values(OrderableField).includes(field as OrderableField);
}

export type DriveValuesGetResponse = {
  field_name: string;
  values: (string | number | null)[];
}[];
export interface FilterSectionInfo extends DropdownSectionInfo {
  filterField: FilterableField;
}
export type FilterSectionState = {
  sectionInfo: FilterSectionInfo;
  values: DropdownData[];
};
// Helper type to remind that one filter is made of multiple
// sections
export type FilterState = FilterSectionState[];

export enum OSFType {
  legacyOsf,
  osf,
}

export type OrgFilterItem = {
  field_name: string;
  values: string[];
};

/// ///////////////////////////////////////////////////////////////////////////
// Publication
/// ///////////////////////////////////////////////////////////////////////////

export type RawPublicationResponse = {
  id: number;
  is_published: boolean;
  is_sample: boolean;
  published_at: IsoDateTime;
  published_by_id: string;
  published_by_name: string;
  title: string;
  updated_at: IsoDateTime;
  updated_by_id: string;
  updated_by_name: string;
  uuid: string;
};

export type PublicationInfo = {
  linkUrl: string;
  publishedAt: Date;
  publishedBy: string;
  title: string;
  updatedAt: Date;
  updatedBy: string;
  publishedById?: number;
  uuid: string;
};

/// ///////////////////////////////////////////////////////////////////////////
// Download File API Responses
/// ///////////////////////////////////////////////////////////////////////////

export interface FileInfo {
  filename: string;
  id: string;
  path: string;
  size: number;
  size_human_readable: string;
  type: string;
}

export interface OutputFileInfo extends FileInfo {
  output_type: string;
  display_name?: string; // For display in download pane if the type does not match the file extension (e.g. 'ply' files are zipped but we want to label it 'ply')
}

/// ///////////////////////////////////////////////////////////////////////////
// Point Viewer Types
/// ///////////////////////////////////////////////////////////////////////////

export type Vector3 = [number, number, number];
export type Quaternion = [number, number, number, number];

export type CameraPose = {
  position: Vector3;
  orbitTarget?: Vector3;
};

export type CameraPreset = {
  pose: CameraPose;
  name: string;
};

export type ColorMap = {
  [sensor: string]: Map<number, [number, number, number]>;
};

export type OsfParams = {
  width: number;
  height: number;
  sensors: string[];
};

export type Pose = {
  rotation: Quaternion; // w, x, y, z
  position: Vector3;
};

export type OsfPose = {
  position: Vector3;
  rotation: Quaternion;
  ts?: number;
};

/**
 *
 * @param mode
 */
export function isColorMode(mode: string) {
  return Object.values(COLOR_MODE).includes(mode as COLOR_MODE);
}

export enum PointShape {
  round = 'Round',
  square = 'Square',
}

export enum PointCloudReturnsCombination {
  return1Only = '1st return',
  return2Only = '2nd return',
  return1And2 = 'Both returns',
  return2Highlighted = 'Highlight 2nd',
}

export enum ImageReturnsCombination {
  return1Only = '1st return',
  return2Only = '2nd return',
}

/// ///////////////////////////////////////////////////////////////////////////
// Layout Types
/// ///////////////////////////////////////////////////////////////////////////
export enum ButtonSize {
  regular,
  small,
  xSmall,
}

/// //////////////////////////////////////////////////////////////////////////
// Architect Scenes
/// //////////////////////////////////////////////////////////////////////////
export type RawArchitectSceneInfo = {
  id: number | string;
  created_at: IsoDateTime;
  created_by: string | null;
  title: string | null;
  publication_uuid?: string | null;
  public_title: string | null;
  tags: string[];
};

export enum ArchitectSceneTableColumn {
  TITLE,
  CREATED_DATE,
  CREATOR,
  TAGS,
}

export type ArchitectSceneInfo = {
  id: number | string;
  createdAt: Date;
  createdBy: string | null;
  title: string | null;
  publicTitle: string | null;
  publicURL?: string;
  tags: string[];
};

export enum ArchitectFilterableField {
  tags = 'tags',
  created_by = 'created_by',
}

export type AsFilterableField = ArchitectFilterableField;

/**
 *
 * @param field
 */
export function isArchitectFilterableField(field: string) {
  return Object.values(ArchitectFilterableField).includes(
    field as ArchitectFilterableField
  );
}

export interface ArchitectFilterSectionInfo extends DropdownSectionInfo {
  filterField: ArchitectFilterableField;
}
export type ArchitectFilterSectionState = {
  sectionInfo: ArchitectFilterSectionInfo;
  values: DropdownData[];
};
// Helper type to remind that one filter is made of multiple
// sections
export type ArchitectFilterState = ArchitectFilterSectionState[];

export type ArchitectSceneValuesGetResponse = {
  field_name: string;
  values: (string | number | null)[];
}[];

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

export interface ExtendedListDriveInfo extends ListDriveInfo {
  access_level: AccessLevel;
  thumbnail_url?: string;
  recording_title?: string;
}

export type OrganizationInfo = {
  org_id: number;
  org_name: string;
  creator_email: string;
  status: 'ACTIVE' | 'INACTIVE' | 'DELETED' | 'MERGED';
};

export type RecordingAccessInfo = {
  access_level: AccessLevel;
  thumbnail?: string;
  title?: string;
};

export type AccessStatus =
  | 'PRIVATE'
  | 'PUBLIC_PUBLIC_NON_FEATURED'
  | 'PUBLIC_PUBLIC_FEATURED'
  | 'PARTNER_PREVIEW'
  | 'PRIVATE_PUBLIC';

export interface MappedOrg {
  label: string;
  value: string;
  data: OrganizationInfo;
}
export interface HoveredPointData {
  index: number;
  screenX: number;
  screenY: number;
  pos1: [number, number, number];
  pos2: [number, number, number] | undefined;
  sn: string;
  hoveredReturn: number;
  channelData: Record<string, number | number[] | undefined>;
  timestamp: string | undefined;
  measurementId: number | undefined;
  asicPixel?: number | undefined;
  azimuthAngle?: number | undefined;
}

export interface SDKSensorInfo {
  sn: string;
  fw_rev: string;
  mode: string;
  prod_line: string;
  prod_pn: string;

  format: SDKDataFormat;

  lidar_data_format?: SDKDataFormat; // OSF is named this instead
  config_params?: {
    // OSF is named this instead
    udp_port_lidar: number;
    udp_port_imu: number;
    lidar_mode: string;
  };

  beam_azimuth_angles: number[];
  beam_altitude_angles: number[];

  // TODO (mw): fix
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  beam_intrinsics?: any;

  // TODO: Update js-side code to translate/handle matrices
  lidar_origin_to_beam_origin_mm: number;
  imu_to_sensor_transform?: number[];
  // Used to correct Z-axis offsets/tilt between the LiDAR and sensor frames when
  // transforming point coordinates (see SDK extrinsics / transform definitions).
  lidar_to_sensor_transform?: number[];
  beam_to_lidar_transform?: number[];

  init_id: number;

  udp_port_lidar: number;
  udp_port_imu: number;
  config?: {
    udp_port_lidar: number;
    udp_port_imu: number;
    lidar_mode: string;
  };
  extrinsics?: number[];
  extrinsic?: number[];
}

// We augment the sensor info object returned by the sdk
// with additional fields that we want to parse and display in the UI
export type SDKSensorInfoAugmented = SDKSensorInfo & {
  // Signal multiplier, azimuth window, and calibration status aren't
  // present in the legacy data format so we make them optional because
  // we cannot always extract them
  signal_multiplier?: number;
  azimuth_window?: number[];
  calibration_status?: CalibrationStatus;
  image_rev?: string;
  hw_rev?: string | null;
  prod_resolution?: string;
  prod_type?: string;
};

export interface ImuIntrinsics {
  imu_to_sensor_transform: number[];
}
